import { AxiosError } from "axios";

import { ADMIN_API_ENDPOINTS } from "@/config/apiEndpoints/adminUrls";
import { handleApiError } from "@/lib/utils";
import adminAxios from "@/lib/axios";

export interface IIconBucket {
  id?: number;
  name: string;
  image_url: string;
  /** Editor-picked grouping — see BUCKET_CATEGORY_OPTIONS. */
  category: string;
  /** Derived and persisted server-side from image_url; see FileTypeKey. */
  file_type?: string;
  created_at?: string;
  updated_at?: string;
}

export interface IIconBucketResponse {
  success: boolean;
  data?: {
    icons?: IIconBucket[];
    icon?: IIconBucket;
  };
  message?: string;
}

/**
 * Get all icons/images from the bucket
 */
export async function getAllIcons() {
  try {
    const response = await adminAxios.get(ADMIN_API_ENDPOINTS.ICON_BUCKET.GET_ALL);
    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

/**
 * Get a single icon/image by ID
 */
export async function getIconById(id: number) {
  try {
    const response = await adminAxios.get(ADMIN_API_ENDPOINTS.ICON_BUCKET.GET_BY_ID(id));
    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

/**
 * Create a new icon/image entry
 */
export async function createIcon(payload: Partial<IIconBucket>) {
  try {
    const response = await adminAxios.post(ADMIN_API_ENDPOINTS.ICON_BUCKET.CREATE, payload);
    return response?.status === 201 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

/**
 * Update an existing icon/image entry
 */
export async function updateIcon(id: number, payload: Partial<IIconBucket>) {
  try {
    const response = await adminAxios.put(ADMIN_API_ENDPOINTS.ICON_BUCKET.UPDATE(id), payload);
    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

/**
 * Delete an icon/image entry
 */
export async function deleteIcon(id: number) {
  try {
    const response = await adminAxios.delete(ADMIN_API_ENDPOINTS.ICON_BUCKET.DELETE(id));
    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

/**
 * Search icons/images by search term
 */
export async function searchIcons(searchTerm: string) {
  try {
    const response = await adminAxios.get(
      `${ADMIN_API_ENDPOINTS.ICON_BUCKET.SEARCH}?q=${encodeURIComponent(searchTerm)}`
    );
    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}
